import { ApolloError, useApolloClient, useMutation } from '@apollo/client';
import { ReactComponent as PackageIcon } from '@bechtel/client-assets/dist/images/empty-state_user-detail.svg';
import { ErrorBoundary, ErrorRetry, PersonaMessages, SuspenseLoader, useName } from '@bechtel/client-common';
import { ASSIGN_UNASSIGN_ASSOCIATED_CREW_MEMBER } from '@bechtel/client-gql';
import { useCrewMember } from '@bechtel/client-weld-common';
import {
  AvatarSize,
  Box,
  ButtonSize,
  Combobox,
  Icon,
  IconSize,
  SideSheet,
  SideSheetBody,
  SideSheetClose,
  SideSheetFooter,
  SideSheetHeader,
  SideSheetSize,
  Spinner,
  Text,
  TextFieldAppearance,
  TextFieldSize,
  theme,
  ToastAction,
  ToastType,
  useToastsDispatch,
} from '@bechtel/gds';
import {
  AssignUnassignAssociatedCrewMembersInput,
  AssignUnassignAssociatedCrewMembersPayload,
  CrewMember,
  CrewMemberList,
  PersonaType,
} from '@bechtel/types';
import { t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { findIndex, get, isEqual, map } from 'lodash';
import React, { FC, Suspense, useDeferredValue, useEffect, useState } from 'react';
import { clearCacheByKey, QueryKeys } from '~/services/cache';
import { AssignUnassignAssociatedCrewMembersErrorType, AssignUnassignError } from './error';
import { AvatarRegularStyled, FooterButtonStyled, HeadingStyled } from './styled';

interface SupportingForemanListProps {
  crewMember: CrewMember;
  crewMembers: CrewMember[];
  close: () => void;
}

const SelectedItemComponent = ({ item }: any) => {
  const { getFullName } = useName();
  return (
    <Box flexDirection={{ sm: 'row' }} alignItems={{ sm: 'center' }}>
      <Box mr={{ sm: '400' }}>
        <AvatarRegularStyled
          hasImageUrl={item && item.imageUrl ? true : false}
          size={AvatarSize.MEDIUM}
          src={item && item.imageUrl ? item.imageUrl : undefined}
        />
      </Box>
      <Box css={{ width: '100%' }} justifyContent={{ sm: 'space-between' }} alignItems={{ sm: 'center' }}>
        <Box flexDirection={{ sm: 'column' }}>
          <HeadingStyled weight="500">{item && getFullName(item.firstName, item.lastName)}</HeadingStyled>
        </Box>
      </Box>
    </Box>
  );
};
const ItemComponent = ({ item }: any) => {
  const { getFullName } = useName();
  const { i18n } = useLingui();
  return (
    <Box flexDirection={{ sm: 'row' }} alignItems={{ sm: 'center' }} key={item?.id}>
      <Box mr={{ sm: '400' }}>
        <AvatarRegularStyled
          hasImageUrl={item && item.imageUrl ? true : false}
          size={AvatarSize.MEDIUM}
          src={item && item.imageUrl ? item.imageUrl : undefined}
        />
      </Box>
      <Box css={{ width: '100%' }} justifyContent={{ sm: 'space-between' }} alignItems={{ sm: 'center' }}>
        <Box flexDirection={{ sm: 'column' }}>
          <HeadingStyled weight="600" css={{ paddingBottom: `${theme.spacings[300]}` }}>
            {getFullName(item.firstName, item.lastName)}
          </HeadingStyled>
        </Box>
      </Box>
    </Box>
  );
};
export const SupportingForemanList: FC<SupportingForemanListProps> = ({ crewMember, crewMembers, close }) => {
  const { i18n } = useLingui();
  const client = useApolloClient();
  const [supportingForemen, setSupportingForemen] = useState<CrewMember[]>([]);
  const [selectedForeman, setSelectedForeman] = useState<CrewMember>();
  const [assignUnassignAssociatedCrewMembersErrors, setAssignUnassignAssociatedCrewMembersErrors] = useState<
    AssignUnassignAssociatedCrewMembersErrorType | ApolloError | null
  >(null);
  const [searchText, setSearchText] = useState('');
  const dispatchToast = useToastsDispatch();
  const debounceSearchQuery = useDeferredValue(searchText);
  const updatedForemanToast = () => {
    return (
      <Box p={{ sm: '500' }} flexDirection={{ sm: 'column' }}>
        <Text family="graphik" weight="600" size="500">
          {t`Successfully updated supporting foreman list.`}
        </Text>
      </Box>
    );
  };

  const { getFullName } = useName();
  const itemToString = (item) => getFullName(item.firstName, item.lastName);
  const closeAndReset = () => {
    setSupportingForemen(crewMembers);
    close();
  };

  const [
    getForemen,
    {
      error: foremanError,
      data: foremanData,
      loading: foremanLoading,
      networkStatus: foremanNetworkStatus,
      refetch: foremanRefetch,
    },
  ] = useCrewMember(true);
  const foremanList = get(foremanData, 'crewMembers.items', []) as CrewMemberList['items'];
  useEffect(() => {
    if (debounceSearchQuery && debounceSearchQuery.length < 3) return;
    getForemen({
      variables: {
        input: {
          searchKeyword: debounceSearchQuery,
          persona: PersonaType.FOREMAN,
        },
        paging: {
          cursor: '1',
          limit: 20,
        },
      },
    });
  }, [debounceSearchQuery, getForemen]);
  const [assignedAssociatedCrewMembers, { loading: mutationLoading }] = useMutation<
    { assignUnassignAssociatedCrewMembersPayload: AssignUnassignAssociatedCrewMembersPayload },
    { input: AssignUnassignAssociatedCrewMembersInput }
  >(ASSIGN_UNASSIGN_ASSOCIATED_CREW_MEMBER, {
    onCompleted: async (data) => {
      const assignUnassignAssociatedCrewMembers = get(data, 'assignUnassignAssociatedCrewMembers');
      const assignUnassignAssociatedCrewMembersErrors = get(assignUnassignAssociatedCrewMembers, 'error');

      if (assignUnassignAssociatedCrewMembersErrors) {
        setAssignUnassignAssociatedCrewMembersErrors(assignUnassignAssociatedCrewMembersErrors);
      } else {
        closeAndReset();
        setAssignUnassignAssociatedCrewMembersErrors(null);
        await client.refetchQueries({
          include: [
            'loadAssociatedCrewMember',
            'commodityWeeklyStats',
            'commodities',
            'commodityFitupStats',
            'commodityStatsClaimedAndUnassignedFWE',
            'commodityStatsRejectedFWE',
            'commodityDashboardTabStats',
            'commodityStatsAllPriorityStats',
          ],
        });
        const toastAction: ToastAction = {
          type: 'ADD_TOAST',
          payload: {
            toastType: ToastType.INFO,
            content: () => updatedForemanToast(),
            autoDismissTimeout: 5000,
          },
        };
        dispatchToast(toastAction);
      }
    },
    update: (cache, result) => {
      if (result.data) {
        clearCacheByKey(cache, QueryKeys.COMMODITIES);
      }
    },

    onError: (error) => {
      if (error) {
        setAssignUnassignAssociatedCrewMembersErrors(error);
      }
    },
  });
  useEffect(() => {
    const updatedForemen = [...supportingForemen, selectedForeman];
    setSupportingForemen(updatedForemen);
  }, [selectedForeman]);
  useEffect(() => {
    setSupportingForemen(crewMembers);
  }, [crewMembers]);

  return (
    <>
      <SideSheet
        onDismiss={closeAndReset}
        size={SideSheetSize.DEFAULT}
        visible
        data-testid="supporting-foreman-sidesheet"
      >
        <SideSheetHeader title={t`Supporting Foreman`}>
          <SideSheetClose onClick={closeAndReset} size={ButtonSize.SMALL}></SideSheetClose>
        </SideSheetHeader>
        <SideSheetBody data-testid="sidesheet-body">
          <Box flexDirection={{ sm: 'column' }} py={{ sm: '500' }} css={{ width: '100%' }}>
            <Box
              px={{ sm: '400' }}
              pb={{ sm: '500' }}
              css={`
                border-bottom: 1px solid ${({ theme }) => theme.colors.grey[300]};
              `}
              data-testid="foreman-search-container"
            >
              {!foremanLoading && foremanError && (
                <ErrorRetry refetch={foremanRefetch}>
                  <Trans>There was a problem loading foreman list</Trans>
                </ErrorRetry>
              )}
              <Combobox
                ariaLabel={t`Foreman`}
                placeholder={t`Add Foreman`}
                size={TextFieldSize.MEDIUM}
                items={foremanList.filter(
                  (foreman) => !supportingForemen.find((supportingForeman) => supportingForeman?.id === foreman?.id)
                )}
                appearance={TextFieldAppearance.NORMAL}
                itemString={itemToString}
                itemComponent={ItemComponent}
                selectedComponent={SelectedItemComponent}
                onSelect={async (selectedItem: any) => {
                  setSelectedForeman(selectedItem);
                }}
                term={searchText}
                setTerm={setSearchText}
                handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setSearchText(event.target.value);
                }}
                loading={foremanLoading || foremanNetworkStatus === foremanNetworkStatus.refetch}
                messageOnNoResults={t`No foreman found`}
              ></Combobox>
            </Box>
            {supportingForemen.length === 0 && (
              <Box
                flexDirection={{ sm: 'column' }}
                alignItems={{ sm: 'center' }}
                justifyContent={{ sm: 'center' }}
                style={{ height: '100%' }}
              >
                <Box pb={{ sm: '600' }} px={{ sm: '600' }}>
                  <Text family="graphik" color="grey.1000" size="250">
                    <Trans>There is no supported foreman selected.</Trans>
                  </Text>
                </Box>
                <PackageIcon />
              </Box>
            )}
            {supportingForemen.length > 0 &&
              map(supportingForemen, (foreman: CrewMember, index) => (
                <Box
                  alignItems={{ sm: 'center' }}
                  justifyContent={{ sm: 'space-between' }}
                  p={{ sm: '400' }}
                  css={`
                    border-bottom: 1px solid ${({ theme }) => theme.colors.grey[300]};
                    width: 100%;
                  `}
                  data-testid={`foreman-item-${index}`}
                >
                  <Box alignItems={{ sm: 'center' }}>
                    <Box mr={{ sm: '400' }}>
                      <AvatarRegularStyled
                        hasImageUrl={foreman && foreman?.imageUrl ? true : false}
                        size={AvatarSize.MEDIUM}
                        src={foreman && foreman?.imageUrl ? foreman.imageUrl : undefined}
                      />
                    </Box>
                    <Box flexDirection={{ sm: 'column' }} css={{ width: '100%' }}>
                      <Box pt={{ sm: '100' }} pb={{ sm: '200' }}>
                        <Text color="grey.1000" size="400" weight="600">
                          {getFullName(
                            foreman?.firstName ? foreman.firstName : ' ',
                            foreman?.lastName ? foreman.lastName : ' '
                          )}
                        </Text>
                      </Box>
                    </Box>
                  </Box>

                  <Icon
                    color="bechtel.blue.800"
                    icon="trash-alt"
                    size={IconSize.MEDIUM}
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                      const selectedIndex = findIndex(supportingForemen, (crew: CrewMember) => crew?.id == foreman?.id);
                      const copySupportingForemen = JSON.parse(JSON.stringify(supportingForemen));
                      if (selectedIndex > -1) {
                        copySupportingForemen.splice(selectedIndex, 1);
                        setSupportingForemen(copySupportingForemen);
                      }
                    }}
                  />
                </Box>
              ))}
          </Box>
        </SideSheetBody>
        <SideSheetFooter>
          <Box justifyContent={{ sm: 'flex-end' }}>
            <Box mr={{ sm: '400' }}>
              <FooterButtonStyled onClick={closeAndReset} data-testid="cancel-button">
                <Trans>Cancel</Trans>
              </FooterButtonStyled>
            </Box>
            <FooterButtonStyled
              data-testid="update-button"
              disabled={isEqual(map(supportingForemen, 'id'), map(crewMembers, 'id'))}
              onClick={() => {
                assignedAssociatedCrewMembers({
                  variables: {
                    input: {
                      associatedCrewMembersIds: map(supportingForemen, 'id'),
                      crewMemberId: crewMember?.id ? crewMember.id : '',
                    },
                  },
                });
              }}
            >
              {mutationLoading && <Spinner />}
              {!mutationLoading && t`Update`}
            </FooterButtonStyled>
          </Box>
        </SideSheetFooter>
      </SideSheet>
      {assignUnassignAssociatedCrewMembersErrors && (
        <ErrorBoundary>
          <Suspense fallback={<SuspenseLoader></SuspenseLoader>}></Suspense>
          <AssignUnassignError
            error={assignUnassignAssociatedCrewMembersErrors}
            close={() => setAssignUnassignAssociatedCrewMembersErrors(null)}
          />
        </ErrorBoundary>
      )}
    </>
  );
};
